import { cn } from '@/utils/cn';
import { ChevronDown } from 'lucide-react';
import { useRef, useState, useEffect } from 'react';

type PaginationLimitProps = {
  value: number;
  options?: number[];
  onChange?: (size: number) => void;
};

const PaginationLimit = ({ value = 10, options = [10, 25, 50], onChange }: PaginationLimitProps) => {
  const [open, setOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpen(false);
        setFocusedIndex(-1);
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  // Handle keyboard navigation
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!open) {
      if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
        event.preventDefault();
        setOpen(true);
        setFocusedIndex(0);
      }
      return;
    }

    switch (event.key) {
      case 'Escape':
        setOpen(false);
        setFocusedIndex(-1);
        buttonRef.current?.focus();
        break;
      case 'ArrowDown':
        event.preventDefault();
        setFocusedIndex((prev) => (prev + 1) % options.length);
        break;
      case 'ArrowUp':
        event.preventDefault();
        setFocusedIndex((prev) => (prev - 1 + options.length) % options.length);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (focusedIndex >= 0) {
          onChange?.(options[focusedIndex]);
          setOpen(false);
          setFocusedIndex(-1);
        }
        break;
    }
  };

  return (
    <div className="relative flex items-center space-x-[10px]  ">
      <span className="text-[10px] font-normal leading-[12px] text-[#25151E]">Show</span>
      <div className="relative" ref={dropdownRef}>
        <button
          ref={buttonRef}
          type="button"
          onClick={() => setOpen(!open)}
          onKeyDown={handleKeyDown}
          className="text-[10px] border border-[#D9D9D9] text-[#25151E] rounded bg-white h-[26px] px-[9px] flex items-center gap-[4px]"
          aria-label="Select items per page"
          aria-expanded={open}
          aria-haspopup="listbox"
        >
          {value}
          <ChevronDown className="w-[10px] h-[10px] shrink-0" />
        </button>

        {open && (
          <ul
            className="absolute z-50  bottom-full mb-1 left-0 w-[100px] bg-white rounded shadow-sm  p-2 space-y-1"
            role="listbox"
            aria-label="Items per page options"
          >
            {options.map((opt, index) => (
              <li key={opt}>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    onChange?.(opt);
                    setOpen(false);
                    setFocusedIndex(-1);
                  }}
                  onKeyDown={handleKeyDown}
                  className={cn(
                    'text-center w-full px-2 py-1 hover:bg-[#0C8DBF] hover:text-[#FFFFFF] rounded text-[10px] cursor-pointer ',
                    {
                      'bg-[#0C8DBF] text-[#FFFFFF]': opt === value,
                      'bg-[#0C8DBF]/20': focusedIndex === index,
                    }
                  )}
                  role="option"
                  aria-selected={opt === value}
                  tabIndex={focusedIndex === index ? 0 : -1}
                >
                  {opt}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default PaginationLimit;
